import { useEffect, useRef } from 'react';
import { useSetAtom, useStore } from 'jotai';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { campaignQueryOptions, campaignTaskTrackingQueryOptions, excludedLocationsQueryOptions } from '@/lib/query-options';
import { HttpError } from '@/lib/apis';
import {
  campaignDataAtom,
  metaCampaignDataAtom,
  campaignTaskTrackingAtom,
  fetchCampaignAtom,
  isLoadingCampaignAtom,
  fetchCampaignTaskTrackingAtom,
} from '@/store';
import { useToast } from '@/hooks/useToast';
import { useCampaignDraft } from '@/hooks/useCampaignDraft';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { useAutoReverseSync } from '@/hooks/useAutoReverseSync';
import { useReverseSyncInvalidation } from '@/hooks/useReverseSyncInvalidation';

export const Route = createFileRoute('/_authenticated/_layout/campaigns/google/$campaignslug/_slug')({
  component: CampaignLayout,
  loader: async ({ params, context }) => {
    const { campaignslug } = params;
    const queryClient = context.queryClient;

    try {
      const campaign = await queryClient.ensureQueryData(campaignQueryOptions(campaignslug));
      return {
        crumb: campaign.name,
      };
    } catch (error) {
      // Only a genuine 404 means "campaign not found" — redirect to the campaigns list.
      // Everything else (500s, network drops, timeouts) must surface in the route error
      // boundary instead of silently masquerading as a deleted campaign (#5001).
      if (error instanceof HttpError && error.status === 404) {
        throw redirect({
          to: '/campaigns',
        });
      }
      throw error;
    }
  },
});

function CampaignLayout() {
  const { campaignslug } = Route.useParams();
  const store = useStore();

  const { isLoading, data: campaign, refetch } = useSuspenseQuery(campaignQueryOptions(campaignslug));

  const { data: campaignTaskTracking, refetch: refetchTracking } = useQuery(
    campaignTaskTrackingQueryOptions(campaign.id),
  );

  // Staged into the draft so excluded-location edits go through Save / the unsaved-changes guard.
  const { data: excludedLocationsData } = useQuery(excludedLocationsQueryOptions(campaign.id));

  const setFetchCampaign = useSetAtom(fetchCampaignAtom);
  const setFetchCampaignTaskTracking = useSetAtom(fetchCampaignTaskTrackingAtom);

  const { toast } = useToast();

  // Sync campaign data to atoms for use by child components
  useEffect(() => {
    store.set(campaignDataAtom, campaign);
    // Selecting a Google campaign clears the Meta selection so /campaigns restores this one (#4660).
    store.set(metaCampaignDataAtom, null);
    store.set(campaignTaskTrackingAtom, campaignTaskTracking);
    store.set(isLoadingCampaignAtom, isLoading);
  }, [store, campaign, campaignTaskTracking, isLoading]);

  // Surface a failed live ad-content sync. When a save's background Google Ads push is rejected
  // (e.g. "Number of Headlines must be 3 or greater"), the task-tracking poll carries it as
  // campaign_ads_ga_sync.failed + latest_error_message — toast it so the user knows the change
  // did not reach Google Ads (the "!" / Deploy button persists separately via budget_state) (#5598).
  const lastAdSyncErrorRef = useRef<string | null>(null);
  useEffect(() => {
    const adSync = campaignTaskTracking?.campaign_ads_ga_sync;
    const errorMsg = adSync?.latest_error_message ?? null;
    if (adSync && adSync.failed > 0 && errorMsg && errorMsg !== lastAdSyncErrorRef.current) {
      lastAdSyncErrorRef.current = errorMsg;
      toast({
        title: 'Changes not applied to Google Ads',
        description: errorMsg,
        variant: 'destructive',
      });
    } else if (adSync && adSync.failed === 0) {
      // A later sync succeeded — reset so a future failure re-toasts even with the same message.
      lastAdSyncErrorRef.current = null;
    }
  }, [campaignTaskTracking, toast]);

  // Set up refetch functions - define inside useEffect to avoid infinite loops
  useEffect(() => {
    const handleRefetch = async () => {
      try {
        const { data } = await refetch();
        store.set(campaignDataAtom, data || null);
      } catch (error) {
        console.error(error);
        toast({ description: 'Failed to fetch campaign', variant: 'destructive' });
      }
    };

    const handleRefetchTracking = async () => {
      try {
        const { data } = await refetchTracking();
        store.set(campaignTaskTrackingAtom, data);
      } catch (error) {
        console.error(error);
      }
    };

    setFetchCampaign(() => handleRefetch);
    setFetchCampaignTaskTracking(() => handleRefetchTracking);
  }, [refetch, refetchTracking, store, setFetchCampaign, setFetchCampaignTaskTracking, toast]);

  // Initialize campaign draft state and unsaved changes warning at the layout level
  // This ensures the draft persists across all campaign sub-pages (ads, keywords, etc.)
  const { isDirty, saveChanges, discardChanges, isSaving } = useCampaignDraft(campaign, excludedLocationsData?.locations);

  // Auto-pull on open (#5566 Req 1): detect Google Ads drift once per mount (throttled 15 min) and
  // pull it in. Mounted here in the shared layout so it covers the overview and ad sub-pages without
  // re-firing per ad (the ad route remounts via key=, this layout does not).
  useAutoReverseSync(campaign);
  // Durable cache invalidation for reverse syncs (manual, auto-pull, or nightly job). Mounted here
  // so it fires once and survives campaign↔ad navigation, unlike the old per-instance block (#5745).
  useReverseSyncInvalidation(campaign);
  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty,
    onSave: saveChanges,
    onDiscard: discardChanges,
    isSaving,
    allowedBasePath: `/campaigns/google/${campaignslug}`,
    contextName: campaign.name,
  });

  // Removed automatic redirect to generating page - users can navigate freely
  return (
    <>
      <Outlet />
      {UnsavedChangesModal}
    </>
  );
}
